import React, { useEffect, useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { useIntl } from 'umi';
import GPT from '@op/components/GPT';
import { usePageInfo } from '@op/hooks/common';
import { useVoiceReportingTextAnalysisDetails } from '../../hooks/useVoiceReport';
import { useVoiceReportingTextAnalysisColumns } from '@op/components/TableCommonHook/hooks/useVoiceReportingTextAnalysisColumns';
import { LanguageType } from '@op/types/common';
import { SortBy } from 'tea-component/lib/table/addons';
import TableCommonHook from '@op/components/TableCommonHook';
import './index.scss';
import { IIntentOptions, transIntent } from '..';
import { downloadAction } from '@op/pages/download/utils';

export default function DetailsBox({
  filter,
  gameInfo,
  intentOptions,
}: {
  filter: any;
  gameInfo: any;
  intentOptions: IIntentOptions[];
}) {
  const { formatMessage } = useIntl();

  const [languages, setLanguages] = useState<Array<LanguageType>>(['origin']);
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    order: 'desc',
    by: 'create_time',
  });
  const [wholeLoading, setWholeLoading] = useState<boolean>(false);
  const filterRef = useRef(null);

  const { pageInfo, changePageInfo } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  const filterParams = useMemo(
    () => ({
      ...filter,
      ...pageInfo,
    }),
    [filter, pageInfo],
  );

  const { data, loading } = useVoiceReportingTextAnalysisDetails({
    filter: filterParams,
  });

  const { columns, sortColumns } = useVoiceReportingTextAnalysisColumns({
    languages,
    setLanguages,
    gameInfo,
    filter,
    transIntent: (intent: string) => transIntent(intentOptions, intent),
  });

  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter;
      setWholeLoading(true);
    }
  }, [filter]);

  useEffect(() => {
    if (wholeLoading && !loading) {
      setWholeLoading(false);
    }
  }, [loading, wholeLoading]);

  return (
    <CardCommon
      className="dbo-voice-report-detail"
      title={formatMessage({ id: 'Details' })}
      loading={wholeLoading}
      loadingHeight={500}
      hasDownload={data?.total > 0}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'VoiceReportingTextAnalysisTable',
          fileName: 'Voice Reporting Text Analysis',
          filter: {
            ...filter,
            ...gameInfo,
          },
          auxiliaryParams: { intentOptions },
        });
      }}
      logBtnId="Details"
    >
      <GPT.PageCard
        contentCard
        className={'gpt-summary dbo-voice-gpt-card'}
        scenario="pubg_voice"
        filter={filter}
        saveName={"Voice Reporting Text Analysis"}
        oriFeedsState={{ total: data?.total }}
      >
        {(toggleVisible: () => void, { loading }: any) => (
          <div className="gpt-summary-entry-tips">
            <GPT.Button
              disabled={loading || (data?.total || 0) <= 0}
              onClick={() => toggleVisible()}
            ></GPT.Button>
          </div>
        )}
      </GPT.PageCard>

      <CardCommon
        className="dbo-voice-report-detail-table"
        contentCard
        emptyHeight={300}
        empty={!(data?.total > 0)}
        loading={loading}
        loadingHeight={300}
      >
        <TableCommonHook
          minWidth={1600}
          recordKey={'comment_uin'}
          tableSticky={true}
          records={data?.detail_list || []}
          total={data?.total || 0}
          pageInfo={pageInfo}
          setPageInfo={changePageInfo}
          columns={columns}
          sortColumns={sortColumns}
          sortInfo={[sortBy, setSortBy]}
        />
      </CardCommon>
    </CardCommon>
  );
}
